// components/LocationDisplay.tsx
import { useState, useEffect } from "react";
import { reverseGeocode } from "layouts/EmployeeTickets/reverseGeocode";
import { Typography } from "@mui/material";
type Location = number | string | null
interface LocationDisplayProps {
  latitude?: Location;
  longitude?: Location;
}

const buildMapsUrl = (latitude?: number, longitude?: number, label?: string) => {
  if (typeof latitude === "number" && typeof longitude === "number") {
    return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
  }

  if (label && label !== "Location data not available" && label !== "Location unknown") {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(label)}`;
  }

  return undefined;
};

const parseCoordinate = (value?: number | string | null): number | undefined => {
  if (typeof value === "number") return value;
  if (typeof value === "string") {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const LocationDisplay: React.FC<LocationDisplayProps> = ({ latitude, longitude }) => {
  const parsedLatitude = parseCoordinate(latitude);
  const parsedLongitude = parseCoordinate(longitude);
  const hasCoordinates =
    typeof parsedLatitude === "number" && typeof parsedLongitude === "number";
  const [location, setLocation] = useState("Loading location...");

  useEffect(() => {
    if (hasCoordinates && parsedLatitude !== undefined && parsedLongitude !== undefined) {
      reverseGeocode(parsedLatitude, parsedLongitude)
        .then(setLocation)
        .catch(() => setLocation("Location unknown"));
    } else {
      setLocation("Location data not available");
    }
  }, [hasCoordinates, parsedLatitude, parsedLongitude]);

  const mapsUrl = buildMapsUrl(parsedLatitude, parsedLongitude, location);

  return (
    <Typography
      variant="caption"
      color="textSecondary"
      component={mapsUrl ? "a" : "span"}
      href={mapsUrl}
      target={mapsUrl ? "_blank" : undefined}
      rel={mapsUrl ? "noopener noreferrer" : undefined}
      sx={{
        cursor: mapsUrl ? "pointer" : "default",
        textDecoration: mapsUrl ? "underline" : "none",
      }}
    >
      {location}
    </Typography>
  );
};

export default LocationDisplay;
